<script setup lang="ts">
import { computed } from 'vue'
import { reactiveOmit } from '@vueuse/core'
import { useI18n } from 'vue-i18n'
import ConfirmDialog from './ConfirmDialog.vue'
import { Button } from '@/shadcn/components/ui/button'
import type { ButtonVariants } from '@/shadcn/components/ui/button'
import type { ConfirmDialogProps, ConfirmForwardEmits } from './types'

interface Props extends ConfirmDialogProps {
  /** Trigger button label (alternative to the default slot). */
  label?: string
  /** Trigger button variant. Defaults to the severity-mapped variant. */
  triggerVariant?: ButtonVariants['variant']
  triggerSize?: ButtonVariants['size']
}

const props = withDefaults(defineProps<Props>(), {
  severity: 'info',
  mode: 'form',
  method: 'POST',
  requireConfirmation: false,
  size: 'sm',
})

const emit = defineEmits<ConfirmForwardEmits>()

const { t } = useI18n()
const resolvedLabel = computed(() => (props.label ? t(props.label) : ''))

const resolvedTriggerVariant = computed<ButtonVariants['variant']>(() => {
  if (props.triggerVariant) return props.triggerVariant
  switch (props.severity) {
    case 'danger':
      return 'destructive'
    case 'success':
      return 'success'
    case 'warning':
      return 'warning'
    default:
      return 'default'
  }
})

/** Only the ConfirmDialog props are forwarded; trigger-only props are stripped. */
const dialogProps = reactiveOmit(props, 'label', 'triggerVariant', 'triggerSize')
</script>

<template>
  <ConfirmDialog
    v-bind="dialogProps"
    @confirmed="emit('confirmed')"
    @success="emit('success', $event)"
    @error="emit('error', $event)"
  >
    <template #trigger>
      <Button :variant="resolvedTriggerVariant" :size="triggerSize">
        <slot>{{ resolvedLabel }}</slot>
      </Button>
    </template>
  </ConfirmDialog>
</template>
